<script setup lang="ts">
import { provide, ref, watch } from "vue";
import { locale } from "../locale";
import { authState } from "../states/auth";
import { getRandomBg } from "../utils/randomBg";
import ThreeDots from "./icons/ThreeDots.vue";
import { currentSpeakers } from "../states/currentSpeakers";
import { UserInRoom } from "../states/usersState.ts";
import {
  isAdmin,
  isSelfAdmin,
} from "../states/adminsState.ts";
import { isHandUp } from "../states/handUpState.ts";
import UserItemControls from "./UserItemControls.vue";
import ToolTip from "./ToolTip.vue";
import { userIsScreenShared } from "../states/screenShareState.ts";
const props = defineProps<{
  user: UserInRoom;
  focused?: boolean;
}>();
const emit = defineEmits<{
  (e: "submenu", val: boolean): void;
}>();
const getAvatarText = (name: string) => {
  let res = "";
  if (name) {
    const nf = name.split(" ");
    res += nf[0][0].toUpperCase();
    if (nf[1] && nf[1].length > 0) {
      res += nf[1][0].toUpperCase();
    }
  }
  return res;
};
const opened = ref(false);
const threeDots = ref<typeof ThreeDots | null>(null)
provide('opened-controls', opened)
watch(opened, () => {
  emit("submenu", opened.value);
});
const toOpen = () => {
  if (isSelfAdmin.value && props.user.id !== authState.id) {
    if (!opened.value) {
      opened.value = true;
    } else {
      opened.value = false;
    }
  }
};
const nameLine = ref()
</script>
<template>
  <div
    ref="item"
    class="user-item-in-popover"
    :class="{
      focused: opened,
      speak: currentSpeakers.includes(user.id),
      admin: isAdmin(user.id),
    }"
  >
    <div
      class="avatar"
      :style="{
        backgroundColor: getRandomBg(getAvatarText(user.displayedName)),
      }"
    >
      <img v-if="user.avatar" :src="user.avatar" alt="" />
      <span v-else class="avatar-text">{{
        getAvatarText(user.displayedName)
      }}</span>
    </div>
    <div class="info">
      <span ref="nameLine" class="main">{{ user.displayedName }}</span>
      <span v-if="isAdmin(user.id)" class="second">{{ locale.leader }}</span>
      <ToolTip :reference="nameLine">{{ user.displayedName }}</ToolTip>
    </div>
    <div v-if="userIsScreenShared(user.id)" class="is-screen-share">
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8 8.92857L9.99747 6.78571M9.99747 6.78571V13.2143M9.99747 6.78571L12 8.92857M10 17.5H3C1.89543 17.5 1 16.6046 1 15.5V4.5C1 3.39543 1.89543 2.5 3 2.5H17C18.1046 2.5 19 3.39543 19 4.5V15.5C19 16.6046 18.1046 17.5 17 17.5H10Z"
          stroke="#8DCA35"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </svg>
    </div>
    <div v-if="isHandUp(user.id)" class="is-hand-up">
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M13.649 6.25532L13.6385 9.2493H12.8994L12.9099 3.73265C12.9103 3.24031 12.5104 2.84047 12.0181 2.84089C11.5253 2.84089 11.1263 3.23989 11.1263 3.73265L11.1158 9.2493H10.3767L10.3872 2.89176C10.3876 2.39942 9.98778 1.99958 9.49544 2C9.00268 2 8.60367 2.399 8.60367 2.89176L8.59316 9.2493H7.85402L7.86453 4.57354C7.86495 4.0812 7.46511 3.68136 6.97277 3.68178C6.48001 3.68178 6.08101 4.08078 6.08101 4.57354L6.07008 12.4325L4.59766 10.9595C4.13306 10.4949 3.38047 10.4949 2.91588 10.9595C2.45129 11.4241 2.45129 12.1767 2.91546 12.6409L6.86556 16.5915C7.77372 17.4997 8.98123 18 10.2661 18C11.6872 18 12.9763 17.4215 13.9097 16.4881C14.8431 15.5547 15.4212 14.266 15.4216 12.8445L15.4321 6.25574C15.4321 5.76297 15.0331 5.36397 14.5403 5.36397C14.0476 5.36397 13.6486 5.76297 13.649 6.25532Z"
          fill="#EFB55F"
        />
      </svg>
    </div>
    <div v-if="!user.micro" class="micro-state">
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5 8.4V9.2C5 10.6852 5.39981 12.0498 6.4 13.1C7.40019 14.1502 8.58551 14.8 10 14.8M10 14.8C11.4145 14.8 12.7498 14.0502 13.75 13C13.7873 12.9608 13.9563 13.081 13.9924 13.0408M10 14.8V18M15 8.4V9.2C15 9.72364 14.9843 10.0052 14.85 10.5M10 18H8.75M10 18H11.4444M3 2L11.7912 10.8273M17 16L13.9924 13.0408M7.8254 6.84524V9.2C7.8254 10.5255 8.73763 11.6 10 11.6C10.6643 11.6 11.3735 11.3024 11.7912 10.8273M11.7912 10.8273L13.607 12.6506L13.9924 13.0408M12.15 7.77778V4.4C12.15 3.05 11.1389 2 10 2C8.75 2 8.2 3 8.04062 3.38197"
          stroke="#A4A4A4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
    <ThreeDots
      v-if="isSelfAdmin && user.id !== authState.id"
      ref="threeDots"
      class="threedots"
      :class="{
        opened,
      }"
      @click="toOpen"
    />
    <UserItemControls :user="user" :control="threeDots?.item"/>
  </div>
</template>
<style scoped>
.user-item-in-popover {
  width: 100%;
  height: 68px;
  padding: 10px 12px;
  display: flex;
  gap: 8px;
  align-items: center;
  border-radius: 4px;
  border: 2px solid transparent;
  transition: border-color ease-in 200ms;
}

.user-item-in-popover.focused {
  border: 2px solid #06a6d0;
  background-color: #494949;
}

.user-item-in-popover.speak {
  border: 2px solid #8dca35;
}

.user-item-in-popover:hover {
  background-color: #494949;
}

.threedots {
  display: none;
  user-select: none;
  cursor: pointer;
}

.threedots.opened {
  display: initial;
  opacity: 1;
  user-select: all;
}

.user-item-in-popover:hover .threedots {
  display: initial;
  opacity: 1;
  user-select: all;
}

.user-item-in-popover.admin:hover .micro-state {
  display: none;
}

.avatar {
  width: 44px;
  height: 44px;
  margin-right: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar img {
  width: 44px;
  height: 44px;
}

.avatar .avatar-text {
  width: 100%;
  text-align: center;
  color: #ffffff;
  font-family: Open Sans;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100px;
  flex-grow: 1;
}

.info .main {
  color: #ececec;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.info .second {
  color: #a4a4a4;
  font-family: Open Sans;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.main,
.second {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.micro-state {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.is-screen-share {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.is-hand-up {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
</style>
